// Cart Subscription Toggle Functionality

class CartSubscriptionManager {
    constructor() {
        this.cartDrawer = document.querySelector('m-cart-drawer');
        this.selectors = {
            toggleWrapper: '.m-subscription-toggle-wrapper',
            select: '[data-subscription-selector]',
            loadingState: '.m-subscription-loading',
            cartItem: '.m-cart-item'
        };

        this.init();
    }

    init() {
        // console.log('Initializing CartSubscriptionManager');

        // Set up event delegation for subscription buttons
        document.addEventListener('click', event => {
            if (event.target.matches('[data-subscription-button]')) {
                this.handleSubscriptionButtonClick(event);
            }
        });

        // Set up event delegation for subscription selectors
        document.addEventListener('change', event => {
            if (event.target.matches(this.selectors.select)) {
                this.handleSubscriptionChange(event);

                // Update the dropdown labels immediately when selection changes
                const select = event.target;
                const optgroups = select.querySelectorAll('optgroup');
                if (optgroups.length > 1) {
                    const selectedOption = select.options[select.selectedIndex];
                    if (selectedOption && selectedOption.dataset.discountText && selectedOption.dataset.planId) {
                        optgroups[1].label = `Subscribe & ${selectedOption.dataset.discountText}`;
                    }
                }
            }
        });

        // Listen for cart updates to re-initialize
        document.addEventListener('cart:updated', () => {
            // Add a delay to ensure DOM has been updated by the cart system
            setTimeout(() => {
                this.refreshSubscriptions();
                // Do an extra check to ensure subscription discounts are visible
                this.initDiscountDisplay();
            }, 300);
        });

        // Initial setup - wait for DOM to be fully ready
        if (document.readyState === 'complete') {
            this.refreshSubscriptions();
            this.updateDropdownLabels();
            this.initDiscountDisplay();
            this.updateSubscriptionButtonText();
        } else {
            window.addEventListener('load', () => {
                this.refreshSubscriptions();
                this.updateDropdownLabels();
                this.initDiscountDisplay();
                this.updateSubscriptionButtonText();
            });
        }

        // MutationObserver to detect cart drawer opening
        this.setupCartDrawerObserver();

        // Populate subscription options for all toggles
        this.populateSubscriptionOptions();
    }

    // Setup observer to detect when cart drawer is opened
    setupCartDrawerObserver() {
        // Look for cart drawer element
        const cartDrawer = document.querySelector('.m-cart-drawer');
        if (!cartDrawer) return;

        // Create a mutation observer to detect when drawer becomes visible
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.attributeName === 'class' &&
                    cartDrawer.classList.contains('is-open')) {
                    // When cart drawer is opened, ensure discount bubbles are showing
                    this.initDiscountDisplay();
                }
            });
        });

        observer.observe(cartDrawer, { attributes: true });
    }

    // Initialize discount display for all subscription items
    initDiscountDisplay() {
        // Get all subscription items
        const subscriptionItems = document.querySelectorAll('.m-cart-item--drawer__wrapper.has-subscription');

        // For each subscription item, ensure discount bubble is showing
        subscriptionItems.forEach(itemWrapper => {
            const cartItem = itemWrapper.closest(this.selectors.cartItem);
            if (!cartItem) return;

            const priceContainer = cartItem.querySelector('.m-cart-item__price');
            if (!priceContainer) return;

            // Check if discount bubble already exists
            const existingBubble = priceContainer.querySelector('.m-subscription-price-savings');
            if (existingBubble) return;

            // Get the toggle wrapper to find item key
            const wrapper = cartItem.querySelector(this.selectors.toggleWrapper);
            if (!wrapper) return;

            const itemKey = wrapper.dataset.itemKey;
            if (!itemKey) return;

            // Get current cart data
            this.getCart().then(cart => {
                const item = cart.items.find(item => item.key === itemKey);
                if (item && item.selling_plan_allocation) {
                    // Update discount display
                    this.updateDiscountDisplay(priceContainer, item.selling_plan_allocation);
                }
            }).catch(err => {
                console.error('Error initializing discount display:', err);
            });
        });
    }

    updateDropdownLabels() {
        const selects = document.querySelectorAll(this.selectors.select);

        selects.forEach(select => {
            // Find the one time purchase option
            const onetimeOption = select.querySelector('option[value="onetime"]');
            if (onetimeOption) {
                onetimeOption.textContent = 'One time purchase';
            }

            // Modify the optgroup labels
            const optgroups = select.querySelectorAll('optgroup');
            if (optgroups.length > 0) {
                // First optgroup label for one-time purchase
                optgroups[0].label = 'One Time Purchase';

                // Second optgroup label for subscription
                if (optgroups.length > 1) {
                    // Always check the currently selected option first
                    const selectedOption = select.options[select.selectedIndex];

                    if (selectedOption && selectedOption.dataset.discountText && selectedOption.dataset.planId) {
                        // Use discount from currently selected plan
                        optgroups[1].label = `Subscribe & ${selectedOption.dataset.discountText}`;
                    } else {
                        // Fallback to first subscription option if no selected option found
                        const subscriptionOption = select.querySelector('option[data-discount-text]');
                        if (subscriptionOption && subscriptionOption.dataset.discountText) {
                            optgroups[1].label = `Subscribe & ${subscriptionOption.dataset.discountText}`;
                        } else {
                            // Default fallback
                            optgroups[1].label = 'Subscribe & Save 15%';
                        }
                    }
                }
            }
        });
    }

    refreshSubscriptions() {
        // Set up a batch of changes to commit

        // Find all subscription toggles in the cart drawer
        const toggles = document.querySelectorAll(this.selectors.toggleWrapper);
        if (toggles.length === 0) return;

        // First, ensure all cart items with subscription data have discounts displayed
        // This helps when discount displays disappear after new items are added
        this.getCart().then(cart => {
            cart.items.forEach(item => {
                if (item.selling_plan_allocation) {
                    // Find the corresponding cart item in the DOM
                    const cartItems = document.querySelectorAll(this.selectors.cartItem);
                    cartItems.forEach(cartItem => {
                        const itemKey = cartItem.dataset.key || cartItem.dataset.itemKey;
                        if (itemKey === item.key) {
                            // Get the price container
                            const priceContainer = cartItem.querySelector('.m-cart-item__price');
                            if (priceContainer) {
                                // Update the discount display with current data
                                this.updateDiscountDisplay(priceContainer, item.selling_plan_allocation);
                            }
                        }
                    });
                }
            });
        }).catch(err => {
            console.error('Error refreshing all subscription discounts:', err);
        });

        toggles.forEach(wrapper => {
            const select = wrapper.querySelector(this.selectors.select);
            if (!select) return;

            // Check if this is a subscription item
            const hasSubscription = select.value && select.value !== 'one-time' && select.value !== 'onetime';
            const cartItem = wrapper.closest(this.selectors.cartItem);

            if (cartItem) {
                // Apply styling for subscription items
                const itemWrapper = cartItem.querySelector('.m-cart-item--drawer__wrapper');
                if (itemWrapper) {
                    if (hasSubscription) {
                        itemWrapper.classList.add('has-subscription');
                    } else {
                        itemWrapper.classList.remove('has-subscription');
                    }
                }

                // Toggle button/dropdown visibility
                const subscriptionButton = wrapper.querySelector('[data-subscription-button]');
                if (subscriptionButton) {
                    if (hasSubscription) {
                        // Show dropdown, hide button
                        select.classList.remove('hidden');
                        subscriptionButton.classList.add('hidden');
                    } else {
                        // Show button, hide dropdown
                        select.classList.add('hidden');
                        subscriptionButton.classList.remove('hidden');
                    }
                }

                if (hasSubscription) {
                    // Get the price container
                    const priceContainer = cartItem.querySelector('.m-cart-item__price');

                    // Get the item's selling plan allocation
                    const itemKey = wrapper.dataset.itemKey;

                    // Fetch cart data to ensure we have current selling plan info
                    this.getCart().then(cart => {
                        const item = cart.items.find(item => item.key === itemKey);
                        if (item && item.selling_plan_allocation) {
                            // Update the discount display with current data
                            this.updateDiscountDisplay(priceContainer, item.selling_plan_allocation);
                        }
                    }).catch(err => {
                        console.error('Error refreshing subscription discount:', err);
                    });
                }
            }
        });

        // Re-populate subscription options
        this.populateSubscriptionOptions();

        // Update dropdown labels to reflect current state
        this.updateDropdownLabels();

        // Update subscription button text
        this.updateSubscriptionButtonText();
    }

    populateSubscriptionOptions() {
        const toggleWrappers = document.querySelectorAll(this.selectors.toggleWrapper);

        toggleWrappers.forEach(wrapper => {
            const productId = wrapper.dataset.productId;
            const variantId = wrapper.dataset.variantId;
            const select = wrapper.querySelector(this.selectors.select);
            const optionsGroup = select?.querySelector('[data-subscription-options]');

            if (productId && variantId && select && optionsGroup) {
                // Check if the options are already populated by Liquid
                const hasPrePopulatedOptions = optionsGroup.children.length > 0;
                const subscriptionToggle = wrapper.closest('.m-cart__subscription-toggle');

                if (hasPrePopulatedOptions) {
                    // Options already populated by Liquid, just make sure the toggle is visible
                    if (subscriptionToggle) {
                        subscriptionToggle.style.display = 'block';
                    }

                    // Ensure all options have discount text attributes
                    Array.from(optionsGroup.querySelectorAll('option')).forEach(option => {
                        if (!option.dataset.discountText && option.textContent) {
                            // Try to extract discount from option text
                            const discountMatch = option.textContent.match(/(\d+)%/);
                            if (discountMatch && discountMatch[1]) {
                                option.dataset.discountText = `${discountMatch[1]}%`;
                            } else {
                                // Default to 15% if we can't extract
                                option.dataset.discountText = '15%';
                            }
                        }
                    });

                    // Update the optgroup label with the discount from the selected option if available
                    const selectedOption = select.options[select.selectedIndex];
                    if (selectedOption && selectedOption.dataset.discountText) {
                        optionsGroup.label = `Subscribe & ${selectedOption.dataset.discountText}`;
                    } else {
                        // Check first subscription option in case selected option doesn't have discount info
                        const firstSubscriptionOption = optionsGroup.querySelector('option[data-discount-text]');
                        if (firstSubscriptionOption && firstSubscriptionOption.dataset.discountText) {
                            optionsGroup.label = `Subscribe & ${firstSubscriptionOption.dataset.discountText}`;
                        } else {
                            // Default to 15% if no discount found
                            optionsGroup.label = 'Subscribe & Save 15%';
                        }
                    }

                    return; // Skip dynamic population
                }

                // Get the currently selected value (if any)
                const currentValue = select.value;
                const hasSubscription = select.hasAttribute('data-has-subscription');

                // Get the selling plans for this product
                this.getSellingPlans(productId, variantId)
                    .then(plans => {
                        // If there are plans, populate the options
                        if (plans && plans.length > 0) {
                            // Clear existing options
                            while (optionsGroup.children.length > 0) {
                                optionsGroup.removeChild(optionsGroup.firstChild);
                            }

                            // Get discount information to update the label
                            let discountText = '15%';
                            if (plans[0].price_adjustments && plans[0].price_adjustments.length > 0) {
                                const adjustment = plans[0].price_adjustments.find(adj => adj.value_type === 'percentage');
                                if (adjustment) {
                                    discountText = `${adjustment.value}%`;
                                } else if (plans[0].price_adjustments[0].value) {
                                    discountText = `${this.formatMoney(plans[0].price_adjustments[0].value)}`;
                                }
                            }

                            // Update the optgroup label with the actual discount
                            optionsGroup.label = `Subscribe & ${discountText}`;

                            // Add new options
                            plans.forEach(plan => {
                                const option = document.createElement('option');
                                option.value = plan.name || plan.id;
                                option.textContent = plan.name || `Delivered every ${plan.options ? plan.options[0] : ''}`;

                                // Make sure to set the data-plan-id attribute
                                if (plan.id) {
                                    option.dataset.planId = plan.id;
                                }

                                // Set discount text attribute for this plan
                                let planDiscountText = '15%';
                                if (plan.price_adjustments && plan.price_adjustments.length > 0) {
                                    const adjustment = plan.price_adjustments.find(adj => adj.value_type === 'percentage');
                                    if (adjustment) {
                                        planDiscountText = `${adjustment.value}%`;
                                    }
                                }
                                option.dataset.discountText = planDiscountText;

                                // If this is the previously selected option, select it
                                if (hasSubscription && (currentValue === option.value || currentValue === plan.id)) {
                                    option.selected = true;
                                    // Update optgroup label with this option's discount
                                    if (planDiscountText) {
                                        optionsGroup.label = `Subscribe & ${planDiscountText}`;
                                    }
                                }

                                optionsGroup.appendChild(option);
                            });

                            // Make sure the subscription toggle is visible
                            if (subscriptionToggle) {
                                subscriptionToggle.style.display = 'block';
                            }
                        } else {
                            // No selling plans found, hide the subscription toggle
                            console.log(`No selling plans found for product ${productId}, variant ${variantId} - hiding subscription toggle`);
                            if (subscriptionToggle) {
                                subscriptionToggle.style.display = 'none';
                            }
                        }
                    })
                    .catch(error => {
                        console.error('Error populating subscription options:', error);
                        // Error getting plans, hide the subscription toggle
                        if (subscriptionToggle) {
                            subscriptionToggle.style.display = 'none';
                        }
                    });
            }
        });
    }

    // Format money value
    formatMoney(cents, format = '{{amount}}') {
        if (typeof cents === 'string') cents = cents.replace('.', '');
        const value = parseInt(cents || 0, 10);
        const amount = (value / 100).toFixed(2);

        return format
            .replace('{{amount}}', amount)
            .replace('{{amount_with_comma_separator}}', amount.replace('.', ','))
            .replace('{{amount_no_decimals}}', Math.floor(amount))
            .replace('{{amount_with_space_separator}}', amount.replace('.', ' '))
            .replace('{{amount_with_period_and_space_separator}}', amount.replace('.', '. '));
    }

    // Handle subscription button click
    handleSubscriptionButtonClick(event) {
        event.preventDefault();
        const button = event.target;
        const wrapper = button.closest(this.selectors.toggleWrapper);

        if (!wrapper) return;

        // Hide the button
        button.classList.add('hidden');

        // Show the dropdown
        const select = wrapper.querySelector(this.selectors.select);
        if (select) {
            select.classList.remove('hidden');

            // Set default to first subscription option (skip the one-time purchase)
            const subscriptionOptions = select.querySelectorAll('optgroup[data-subscription-options] option');
            if (subscriptionOptions.length > 0) {
                // Select the first subscription option
                subscriptionOptions[0].selected = true;

                // Apply subscription change via AJAX
                const itemKey = wrapper.dataset.itemKey;
                const variantId = wrapper.dataset.variantId;
                const sellingPlanId = subscriptionOptions[0].dataset.planId;

                if (sellingPlanId) {
                    this.showLoading(wrapper, true);

                    // Get current cart item
                    this.getCart().then(cart => {
                        const cartItem = cart.items.find(item =>
                            item.key === itemKey ||
                            item.variant_id.toString() === variantId
                        );

                        if (cartItem) {
                            // Update cart item with subscription
                            this.updateCartItem(cartItem.key, cartItem.quantity, sellingPlanId)
                                .then(response => {
                                    this.hideLoading(wrapper);
                                    this.handleCartUpdateResponse(response, wrapper, true);
                                })
                                .catch(error => {
                                    console.error('Error updating subscription:', error);
                                    this.hideLoading(wrapper);
                                    this.showErrorWithinDropdown(wrapper, 'Failed to update subscription');

                                    // Revert UI on error
                                    button.classList.remove('hidden');
                                    select.classList.add('hidden');
                                });
                        } else {
                            console.error('Item not found in cart');
                            this.hideLoading(wrapper);
                            this.showErrorWithinDropdown(wrapper, 'Item not found in cart');

                            // Revert UI on error
                            button.classList.remove('hidden');
                            select.classList.add('hidden');
                        }
                    }).catch(error => {
                        console.error('Error getting cart:', error);
                        this.hideLoading(wrapper);
                        this.showErrorWithinDropdown(wrapper, 'Error getting cart data');

                        // Revert UI on error
                        button.classList.remove('hidden');
                        select.classList.add('hidden');
                    });
                } else {
                    console.error('No selling plan ID found for subscription option');

                    // Trigger the change event to apply the subscription if no selling plan ID found
                    // This fallback will use the regular change handler
                    const changeEvent = new Event('change', { bubbles: true });
                    select.dispatchEvent(changeEvent);
                }
            } else {
                console.error('No subscription options found');

                // Revert UI
                button.classList.remove('hidden');
                select.classList.add('hidden');
            }
        }
    }

    async handleSubscriptionChange(event) {
        event.preventDefault();
        const wrapper = event.target.closest(this.selectors.toggleWrapper);
        const select = event.target;
        const itemKey = wrapper.dataset.itemKey;
        const variantId = wrapper.dataset.variantId;
        const isSubscription = select.value !== 'onetime';

        // Handle UI for one-time purchase selection
        if (!isSubscription) {
            // If switching to one-time purchase, show button and hide dropdown after API call
            const subscriptionButton = wrapper.querySelector('[data-subscription-button]');
            if (subscriptionButton) {
                // We'll toggle visibility after the API call succeeds
                // to avoid flickering if there's an error
            }
        }

        this.showLoading(wrapper, isSubscription);

        // Find current line item in cart
        const cart = await this.getCart();
        const cartItem = cart.items.find(item =>
            item.key === itemKey || item.variant_id.toString() === variantId
        );

        if (!cartItem) {
            this.hideLoading(wrapper);
            this.showErrorWithinDropdown(wrapper, 'Item not found in cart');
            return;
        }

        try {
            let response;

            if (isSubscription) {
                // Get selling plan ID directly from the selected option
                const selectedOption = select.options[select.selectedIndex];
                const sellingPlanId = selectedOption.dataset.planId;

                console.log(`Selected selling plan ID: ${sellingPlanId}`);

                if (!sellingPlanId) {
                    this.hideLoading(wrapper);
                    this.showErrorWithinDropdown(wrapper, 'Subscription plan not found');
                    return;
                }

                // Update product from one-time to subscription
                this.showLoading(wrapper, true); // true = upgrading to subscription
                response = await this.updateCartItem(cartItem.key, cartItem.quantity, sellingPlanId);
                this.showSuccessIndicationInDropdown(select, wrapper);
            } else {
                // Update product from subscription to one-time
                this.showLoading(wrapper, false); // false = downgrading to one-time
                response = await this.updateCartItem(cartItem.key, cartItem.quantity, '');
                this.showSuccessIndicationInDropdown(select, wrapper);

                // Now that API call succeeded, update UI for one-time purchase
                const subscriptionButton = wrapper.querySelector('[data-subscription-button]');
                if (subscriptionButton) {
                    subscriptionButton.classList.remove('hidden');
                    select.classList.add('hidden');
                }
            }

            // Handle response and update UI
            this.hideLoading(wrapper);
            this.handleCartUpdateResponse(response, wrapper, isSubscription);

        } catch (error) {
            console.error('Error updating cart item:', error);
            this.hideLoading(wrapper);
            this.showErrorWithinDropdown(wrapper, 'Failed to update subscription');
        }
    }

    // Show a brief success indication within the dropdown
    showSuccessIndicationInDropdown(select, wrapper) {
        // Add a temporary success class to the wrapper
        wrapper.classList.add('subscription-update-success');

        // Remove the class after a short delay
        setTimeout(() => {
            wrapper.classList.remove('subscription-update-success');
        }, 2000);
    }

    // Show error within the dropdown area
    showErrorWithinDropdown(wrapper, message) {
        console.error(message);

        // Add error class to wrapper
        wrapper.classList.add('subscription-update-error');

        // Create or update error message
        let errorElement = wrapper.querySelector('.subscription-error-message');
        if (!errorElement) {
            errorElement = document.createElement('div');
            errorElement.className = 'subscription-error-message';
            wrapper.appendChild(errorElement);
        }

        errorElement.textContent = message;

        // Remove the error after a delay
        setTimeout(() => {
            wrapper.classList.remove('subscription-update-error');
            if (errorElement && errorElement.parentNode) {
                errorElement.remove();
            }
        }, 3000);
    }

    hideLoading(wrapper) {
        wrapper.classList.remove('is-loading');
        const loadingElement = wrapper.querySelector(this.selectors.loadingState);
        if (loadingElement) loadingElement.style.display = 'none';
    }

    showLoading(wrapper, isSubscription) {
        // Add loading class to wrapper to hide select text
        wrapper.classList.add('is-loading');

        const loadingElement = wrapper.querySelector(this.selectors.loadingState);
        if (loadingElement) {
            // Set the appropriate loading text based on the action
            const loadingText = loadingElement.querySelector('.m-subscription-loading-text');
            if (loadingText) {
                if (isSubscription === true) {
                    loadingText.textContent = 'Upgrading...';
                } else if (isSubscription === false) {
                    loadingText.textContent = 'Downgrading...';
                } else {
                    loadingText.textContent = 'Updating...';
                }
            }

            loadingElement.style.display = 'flex';
        }
    }

    handleCartUpdateResponse(response, wrapper, isSubscription) {
        // Update the UI to reflect the change
        this.hideLoading(wrapper);

        const select = wrapper.querySelector(this.selectors.select);
        if (select) {
            if (isSubscription) {
                select.setAttribute('data-has-subscription', 'true');
            } else {
                select.removeAttribute('data-has-subscription');
            }
        }

        // Find the cart item and update its state
        const cartItem = wrapper.closest(this.selectors.cartItem);
        if (cartItem) {
            const itemWrapper = cartItem.querySelector('.m-cart-item--drawer__wrapper');
            if (itemWrapper) {
                itemWrapper.classList.toggle('has-subscription', isSubscription);
            }

            // Get the updated item from the cart response
            this.updateCartItemDisplay(cartItem, response, isSubscription);
        }

        // Trigger cart update event
        document.dispatchEvent(new CustomEvent('cart:updated', {
            detail: { cart: response }
        }));

        // Make sure subscription options are properly populated
        this.populateSubscriptionOptions();
    }

    /**
     * Update the cart item display with the updated price and discount information
     * @param {HTMLElement} cartItem - The cart item element to update
     * @param {Object} cartResponse - The cart response from the API
     * @param {Boolean} isSubscription - Whether this is a subscription or not
     */
    updateCartItemDisplay(cartItem, cartResponse, isSubscription) {
        // Get the item key from the wrapper
        const wrapper = cartItem.querySelector(this.selectors.toggleWrapper);
        if (!wrapper) return;

        const itemKey = wrapper.dataset.itemKey;
        const variantId = wrapper.dataset.variantId;

        // Find the item in the cart response by key first
        let updatedItem = cartResponse.items.find(item => item.key === itemKey);

        // If not found by key, try to find by variant ID 
        // (keys can change after selling plan changes)
        if (!updatedItem && variantId) {
            updatedItem = cartResponse.items.find(item =>
                item.variant_id.toString() === variantId.toString()
            );
        }

        // If still not found, check if we stored the variant ID during the update
        if (!updatedItem && cartResponse._lastUpdatedVariantId) {
            updatedItem = cartResponse.items.find(item =>
                item.variant_id.toString() === cartResponse._lastUpdatedVariantId.toString()
            );
        }

        if (!updatedItem) {
            console.error('Could not find updated item in cart response', cartResponse.items);
            // Instead of refreshing, try to get the latest cart and retry
            this.getCart().then(cart => {
                // Try with both the original key and variant ID
                const retryItem = cart.items.find(item =>
                    item.key === itemKey ||
                    item.variant_id.toString() === variantId.toString()
                );

                if (retryItem) {
                    console.log('Found item in fresh cart data, retrying display update');

                    // Update the wrapper with the new key
                    if (retryItem.key !== itemKey) {
                        console.log(`Updating item key from ${itemKey} to ${retryItem.key}`);
                        wrapper.dataset.itemKey = retryItem.key;
                    }

                    this._updateCartItemDisplayWithItem(cartItem, retryItem, wrapper);

                    // Update cart subtotal while we're here
                    this.updateCartSubtotal(cart.total_price);
                } else {
                    console.error('Still could not find item, showing error state');
                    this.hideLoading(wrapper);
                    this.showMessage(wrapper, 'Could not update cart. Please refresh the page.', 'error');
                    setTimeout(() => {
                        this.hideMessage();
                        window.location.reload();
                    }, 3000);
                }
            }).catch(err => {
                console.error('Error getting fresh cart data:', err);
                this.hideLoading(wrapper);
                this.showMessage(wrapper, 'Error updating cart. Please refresh the page.', 'error');
                setTimeout(() => {
                    this.hideMessage();
                    window.location.reload();
                }, 3000);
            });
            return;
        }

        // If we found the item but it has a new key, update the wrapper
        if (updatedItem.key !== itemKey) {
            console.log(`Updating item key from ${itemKey} to ${updatedItem.key}`);
            wrapper.dataset.itemKey = updatedItem.key;
        }

        this._updateCartItemDisplayWithItem(cartItem, updatedItem, wrapper);

        // Get cart subtotal and update it
        this.updateCartSubtotal(cartResponse.total_price);

        // Update quantity if needed
        const quantityInput = cartItem.querySelector('.m-quantity__input');
        if (quantityInput && quantityInput.value != updatedItem.quantity) {
            quantityInput.value = updatedItem.quantity;
        }
    }

    /**
     * Helper method to update cart item display with a specific item
     * @private
     */
    _updateCartItemDisplayWithItem(cartItem, updatedItem, wrapper) {
        // Double-check if item still has a subscription
        const hasSubscription = updatedItem.selling_plan_allocation != null;
        console.log('Item subscription status:', hasSubscription ? 'Has subscription' : 'No subscription');

        // Update select data-has-subscription attribute to match actual state
        const select = wrapper.querySelector(this.selectors.select);
        if (select) {
            if (hasSubscription) {
                select.setAttribute('data-has-subscription', 'true');

                // Make sure the right option is selected
                const options = select.querySelectorAll('option');
                for (let i = 0; i < options.length; i++) {
                    const option = options[i];
                    if (option.dataset.planId === updatedItem.selling_plan_allocation.selling_plan.id) {
                        option.selected = true;
                        break;
                    }
                }
            } else {
                select.removeAttribute('data-has-subscription');
                // Make sure the one-time option is selected
                const onetimeOption = select.querySelector('option[value="onetime"]');
                if (onetimeOption) {
                    onetimeOption.selected = true;
                }
            }
        }

        // Update the price display
        const priceContainer = cartItem.querySelector('.m-cart-item__price');
        if (!priceContainer) return;

        if (hasSubscription && updatedItem.selling_plan_allocation) {
            // Format prices for display
            const regularPrice = this.formatMoney(updatedItem.selling_plan_allocation.compare_at_price);
            const finalPrice = this.formatMoney(updatedItem.price);

            // Get discount percentage for use in discount bubble
            let discountPercentage = 0;
            let hasDiscount = false;

            if (updatedItem.selling_plan_allocation &&
                updatedItem.selling_plan_allocation.price_adjustments &&
                updatedItem.selling_plan_allocation.price_adjustments.length > 0) {

                const percentageAdjustment = updatedItem.selling_plan_allocation.price_adjustments.find(
                    adj => adj.value_type === 'percentage'
                );

                if (percentageAdjustment && percentageAdjustment.value > 0) {
                    discountPercentage = percentageAdjustment.value;
                    hasDiscount = true;
                }
            }

            // If no explicit percentage discount but there's a price difference, calculate it
            if (!hasDiscount && updatedItem.selling_plan_allocation) {
                const comparePrice = updatedItem.selling_plan_allocation.compare_at_price;
                const price = updatedItem.price;

                if (comparePrice && price && comparePrice > price) {
                    discountPercentage = Math.round((comparePrice - price) / comparePrice * 100);
                    hasDiscount = true;
                }
            }

            // Set a default discount if we couldn't calculate one
            if (!hasDiscount && select) {
                // Try to get discount from selected option
                const selectedOption = select.options[select.selectedIndex];
                if (selectedOption && selectedOption.dataset.discountText) {
                    const discountMatch = selectedOption.dataset.discountText.match(/(\d+)%/);
                    if (discountMatch && discountMatch[1]) {
                        discountPercentage = parseInt(discountMatch[1], 10);
                        hasDiscount = true;
                    }
                }

                // If still no discount, default to 15%
                if (!hasDiscount) {
                    discountPercentage = 15;
                    hasDiscount = true;
                }
            }

            // Update subscription price HTML with the correct structure
            let priceHTML = `
                <div class="m-cart-item__price-subscription">
                    <div class="price-row">
                        <span class="m-cart-item__price--final">${finalPrice}</span>
                        <del class="m-cart-item__price--regular subscription-compare-price">${regularPrice}</del>
                    </div>`;

            // Always add discount bubble for subscriptions
            priceHTML += `
                <div class="m-subscription-price-savings">
                    <span class="m-subscription-discount-percentage">${discountPercentage}% off</span>
                </div>`;

            priceHTML += `</div>`;
            priceContainer.innerHTML = priceHTML;
        } else {
            // Regular price (one-time purchase)
            const regularPrice = this.formatMoney(updatedItem.original_price);
            const finalPrice = this.formatMoney(updatedItem.final_price);

            if (updatedItem.original_price !== updatedItem.final_price) {
                // Item has a discount (not subscription related)
                priceContainer.innerHTML = `
                    <div class="m-cart-item__price-discounted">
                        <del class="m-cart-item__price--regular">${regularPrice}</del>
                        <span class="m-cart-item__price--final">${finalPrice}</span>
                    </div>`;
            } else {
                // Regular one-time price (no discount)
                priceContainer.innerHTML = `
                    <div class="m-cart-item__price-regular">
                        <span class="m-cart-item__price--final">${regularPrice}</span>
                    </div>`;
            }
        }

        // Update quantity if needed
        const quantityInput = cartItem.querySelector('.m-quantity__input');
        if (quantityInput && quantityInput.value != updatedItem.quantity) {
            quantityInput.value = updatedItem.quantity;
        }

        // Update the optgroup label
        if (select) {
            this.updateDropdownLabels();
        }
    }

    /**
     * Update the discount display for a subscription item
     * @param {HTMLElement} priceContainer - The price container element
     * @param {Object} sellingPlanAllocation - The selling plan allocation data
     */
    updateDiscountDisplay(priceContainer, sellingPlanAllocation) {
        console.log('Updating discount display with:', sellingPlanAllocation);

        // Remove any existing discount display
        const existingDiscount = priceContainer.querySelector('.m-subscription-price-savings');
        if (existingDiscount) {
            existingDiscount.remove();
        }

        // Default to 15% discount if we can't find specific values
        let discountPercentage = 15;
        let discountAmount = 0;

        // Try to get from price_adjustments
        if (sellingPlanAllocation && sellingPlanAllocation.price_adjustments && sellingPlanAllocation.price_adjustments.length > 0) {
            // Find percentage adjustment
            const percentageAdjustment = sellingPlanAllocation.price_adjustments.find(
                adj => adj.value_type === 'percentage'
            );

            if (percentageAdjustment && percentageAdjustment.value > 0) {
                discountPercentage = percentageAdjustment.value;
            }

            // Find fixed amount adjustment
            const fixedAdjustment = sellingPlanAllocation.price_adjustments.find(
                adj => adj.value_type === 'fixed_amount' && adj.value > 0
            );

            if (fixedAdjustment) {
                discountAmount = fixedAdjustment.value;
            }
        }

        // If we still don't have a discount, calculate from price difference
        if (discountPercentage === 0 && sellingPlanAllocation) {
            const regularPrice = sellingPlanAllocation.compare_at_price || sellingPlanAllocation.regular_price;
            const subscriptionPrice = sellingPlanAllocation.price;

            if (regularPrice && subscriptionPrice && regularPrice > subscriptionPrice) {
                discountAmount = regularPrice - subscriptionPrice;
                discountPercentage = Math.round((discountAmount / regularPrice) * 100);
            }
        }

        // Always create and insert a discount display - default to 15% if no specific value
        const discountDiv = document.createElement('div');
        discountDiv.className = 'm-subscription-price-savings';

        if (discountAmount > 0 && discountPercentage === 0) {
            discountDiv.innerHTML = `
                <span class="m-subscription-discount-amount">${this.formatMoney(discountAmount)} off</span>
            `;
        } else {
            // Default to percentage display, using 15% if no specific value found
            discountDiv.innerHTML = `
                <span class="m-subscription-discount-percentage">${discountPercentage}% off</span>
            `;
        }

        priceContainer.appendChild(discountDiv);
    }

    /**
     * Update the cart subtotal display
     * @param {Number} totalPrice - The total price of the cart
     */
    updateCartSubtotal(totalPrice) {
        const subtotalElement = document.querySelector('[data-cart-subtotal-price]');
        if (subtotalElement) {
            subtotalElement.innerHTML = this.formatMoney(totalPrice);
        }
    }

    /**
     * Format money value for display
     * @param {Number|String} cents - The price in cents
     * @returns {String} - Formatted price string
     */
    formatMoney(cents) {
        if (typeof cents === 'string') {
            cents = cents.replace('.', '');
        }
        const value = parseInt(cents || 0, 10);

        // Check if we need to use the theme's money format
        if (typeof window.theme !== 'undefined' && window.theme.moneyFormat) {
            const amount = (value / 100).toFixed(2);
            return window.theme.moneyFormat
                .replace('{{amount}}', amount)
                .replace('{{amount_with_comma_separator}}', amount.replace('.', ','))
                .replace('{{amount_no_decimals}}', Math.floor(amount))
                .replace('{{amount_with_space_separator}}', amount.replace('.', ' '));
        }

        // Fallback to simple formatting
        return '$' + (value / 100).toFixed(2);
    }

    // Get the current cart
    getCart() {
        return fetch(`${window.Shopify.routes.root}cart.js`)
            .then(response => response.json());
    }

    // Update a cart item
    updateCartItem(key, quantity, selling_plan_id) {
        console.log(`Updating cart item: key=${key}, quantity=${quantity}, selling_plan_id=${selling_plan_id || 'none'}`);

        // Use a very direct approach with Shopify's jQuery API if available
        if (window.Shopify && window.Shopify.updateCartItem) {
            return new Promise((resolve, reject) => {
                const variantId = key.split(':')[0];
                const properties = {};

                window.Shopify.updateCartItem(key, {
                    quantity: quantity,
                    selling_plan: selling_plan_id || '',
                    properties: properties
                }, (cart) => {
                    console.log('Shopify direct API cart update successful:', cart);
                    cart._lastUpdatedVariantId = variantId;
                    resolve(cart);
                }, (error) => {
                    console.error('Shopify direct API cart update error:', error);
                    reject(error);
                });
            });
        }

        // Extract variant id from the key if possible
        const variantId = key.split(':')[0];

        // Try the most direct approach first with jQuery
        if (window.jQuery) {
            return new Promise((resolve, reject) => {
                const params = {
                    type: 'POST',
                    url: '/cart/change.js',
                    dataType: 'json',
                    data: {
                        id: key,
                        quantity: quantity
                    },
                    success: function (cart) {
                        console.log('jQuery cart update successful:', cart);
                        cart._lastUpdatedVariantId = variantId;
                        resolve(cart);
                    },
                    error: function (error) {
                        console.error('jQuery cart update error:', error);
                        // Try our fallback
                        this.alternativeCartUpdate(key, quantity, selling_plan_id)
                            .then(resolve)
                            .catch(reject);
                    }.bind(this)
                };

                // Add selling plan if specified
                if (selling_plan_id) {
                    params.data.selling_plan = selling_plan_id;
                }

                window.jQuery.ajax(params);
            });
        }

        // Fallback to fetch
        try {
            const data = {
                id: key,
                quantity: quantity
            };

            // For subscription -> one-time, we need to explicitly set selling_plan to null (not undefined)
            if (selling_plan_id) {
                data.selling_plan = selling_plan_id;
            } else {
                data.selling_plan = '';  // Empty string to clear selling plan
            }

            console.log('Cart update data:', data);

            return fetch(`${window.Shopify.routes.root}cart/change.js`, {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest'
                },
                body: JSON.stringify(data)
            })
                .then(response => {
                    if (!response.ok) {
                        if (response.status === 400) {
                            // For 400 errors, try an alternative approach using a form data post
                            console.log('Received 400 response, trying alternative approach');
                            return this.alternativeCartUpdate(key, quantity, selling_plan_id);
                        }
                        throw new Error(`HTTP error! Status: ${response.status}`);
                    }
                    return response.json();
                })
                .then(cart => {
                    console.log('Cart update successful:', cart);

                    // Store the variant ID we were working with
                    cart._lastUpdatedVariantId = variantId;

                    // Update the mini-cart count if it exists
                    if (typeof window.MinimogTheme !== 'undefined' && window.MinimogTheme.Cart) {
                        window.MinimogTheme.Cart.updateCartCount(cart.item_count);
                    }

                    // Trigger cart update event for any listeners
                    document.dispatchEvent(new CustomEvent('cart:updated', {
                        detail: { cart: cart }
                    }));

                    return cart;
                });
        } catch (error) {
            console.error('Error in updateCartItem:', error);
            return Promise.reject(error);
        }
    }

    /**
     * Alternative cart update method using FormData
     * @param {string} key - Cart item key
     * @param {number} quantity - Item quantity
     * @param {string|null} selling_plan_id - Selling plan ID
     * @returns {Promise} - Promise resolving to cart data
     */
    alternativeCartUpdate(key, quantity, selling_plan_id) {
        console.log('Using alternative cart update method');

        // Extract variant id from the key if possible
        const variantId = key.split(':')[0];

        const formData = new FormData();
        formData.append('id', key);
        formData.append('quantity', quantity);

        if (selling_plan_id) {
            formData.append('selling_plan', selling_plan_id);
        } else {
            formData.append('selling_plan', '');
        }

        // Use jQuery AJAX if available for better compatibility
        if (window.jQuery) {
            return new Promise((resolve, reject) => {
                window.jQuery.ajax({
                    type: 'POST',
                    url: `${window.Shopify.routes.root}cart/change`,
                    data: formData,
                    processData: false,
                    contentType: false,
                    success: function (data) {
                        console.log('jQuery cart update successful:', data);

                        // Since we might not get a JSON response directly, get the cart
                        fetch(`${window.Shopify.routes.root}cart.js`)
                            .then(response => response.json())
                            .then(cart => {
                                cart._lastUpdatedVariantId = variantId;
                                resolve(cart);
                            })
                            .catch(reject);
                    },
                    error: function (error) {
                        console.error('jQuery cart update error:', error);
                        reject(error);
                    }
                });
            });
        }

        // Fallback to fetch API
        return fetch(`${window.Shopify.routes.root}cart/change`, {
            method: 'POST',
            credentials: 'same-origin',
            body: formData
        })
            .then(response => {
                if (!response.ok) {
                    throw new Error(`HTTP error in alternative update! Status: ${response.status}`);
                }
                return this.getCart(); // Get fresh cart data
            })
            .then(cart => {
                console.log('Alternative cart update successful:', cart);

                // Store the variant ID we were working with
                cart._lastUpdatedVariantId = variantId;

                return cart;
            });
    }

    // Get selling plans for a product
    getSellingPlans(productId, variantId) {
        console.log(`Getting selling plans for product ${productId}, variant ${variantId}`);

        return new Promise((resolve, reject) => {
            try {
                // First check if Awtomic subscription data is available via metafields
                const awtomicPlanMetafield = document.querySelector(`[data-awtomic-plans][data-product-id="${productId}"]`);
                if (awtomicPlanMetafield) {
                    try {
                        const awtomicPlans = JSON.parse(awtomicPlanMetafield.getAttribute('data-awtomic-plans'));
                        console.log('Found Awtomic plans via metafield:', awtomicPlans);

                        // Sort selling plans by delivery frequency if possible
                        const sortedPlans = this.sortSellingPlansByFrequency(awtomicPlans);
                        resolve(sortedPlans);
                        return;
                    } catch (error) {
                        console.warn('Error parsing Awtomic plans from metafield:', error);
                    }
                }

                // Make sure variantId is treated as a number for comparison
                variantId = parseInt(variantId, 10);

                // Fetch product data from Shopify
                fetch(`/products/${productId}.js`)
                    .then(response => {
                        if (!response.ok) {
                            if (response.status === 404) {
                                console.log(`Product ${productId} not found, checking fallback URL`);
                                // Try alternative URL format
                                return fetch(`/products.json?ids=${productId}`);
                            }
                            throw new Error(`HTTP error! Status: ${response.status}`);
                        }
                        return response.json();
                    })
                    .then(data => {
                        let product;

                        // Check if we're using the fallback format
                        if (data.products) {
                            product = data.products[0];
                        } else {
                            product = data.product || data;
                        }

                        if (!product) {
                            console.log(`No product data found for ${productId}`);
                            resolve([]);
                            return;
                        }

                        console.log(`Fetched product data for ${product.title || 'Unknown product'}`);

                        // Check if product has selling plans directly from Shopify
                        if (product.selling_plan_groups && product.selling_plan_groups.length > 0) {
                            console.log(`Found ${product.selling_plan_groups.length} selling plan groups`);

                            // Flatten all selling plans from all groups
                            const sellingPlans = [];
                            product.selling_plan_groups.forEach(group => {
                                group.selling_plans.forEach(plan => {
                                    // Check if this plan is valid for the current variant
                                    const isValidForVariant = !product.requires_selling_plan ||
                                        !plan.variants ||
                                        plan.variants.includes(variantId);

                                    if (isValidForVariant) {
                                        sellingPlans.push(plan);
                                    }
                                });
                            });

                            console.log(`Found ${sellingPlans.length} applicable selling plans for variant ${variantId}`);

                            // Sort selling plans by delivery frequency before returning
                            const sortedPlans = this.sortSellingPlansByFrequency(sellingPlans);
                            resolve(sortedPlans);
                        } else {
                            console.log(`No selling plan groups found for product ${productId}`);
                            resolve([]);
                        }
                    })
                    .catch(error => {
                        console.error('Error getting selling plans:', error);
                        // Return empty array instead of rejecting to avoid breaking the subscription flow
                        resolve([]);
                    });
            } catch (error) {
                console.error('Error getting selling plans:', error);
                // Return empty array instead of rejecting to avoid breaking the subscription flow
                resolve([]);
            }
        });
    }

    // Add a new method to sort selling plans by delivery frequency
    sortSellingPlansByFrequency(plans) {
        // Make a copy to avoid modifying the original array
        const sortedPlans = [...plans];

        // Extract frequency and sort
        return sortedPlans.sort((a, b) => {
            // Get delivery frequencies from plan names or options
            const freqA = this.extractDeliveryFrequency(a);
            const freqB = this.extractDeliveryFrequency(b);

            // Sort numerically by the extracted frequency
            return freqA - freqB;
        });
    }

    // Helper method to extract delivery frequency from a selling plan
    extractDeliveryFrequency(plan) {
        // Default value if we can't extract frequency
        let frequency = 999;

        // Try to extract from plan name first
        if (plan.name) {
            const nameMatch = plan.name.match(/Deliver every (\d+) days/i) ||
                plan.name.match(/every (\d+) days/i) ||
                plan.name.match(/(\d+) days/i);
            if (nameMatch && nameMatch[1]) {
                return parseInt(nameMatch[1], 10);
            }
        }

        // Try to extract from options
        if (plan.options && plan.options.length > 0) {
            for (const option of plan.options) {
                const optionMatch = option.match(/(\d+) days/i);
                if (optionMatch && optionMatch[1]) {
                    return parseInt(optionMatch[1], 10);
                }
            }
        }

        // If we can't extract a frequency, return the default
        return frequency;
    }

    // Find the selling plan that matches the selected frequency
    findMatchingSellingPlan(plans, selectedOption) {
        // Extract the frequency from selectedOption
        const frequencyMatch = selectedOption.match(/(\d+)\s*weeks/i);
        if (!frequencyMatch) return null;

        const frequency = frequencyMatch[1];

        // Find a plan that matches this frequency
        return plans.find(plan => {
            // Check if the plan name contains the frequency
            if (plan.name.toLowerCase().includes(`${frequency} weeks`)) {
                return true;
            }

            // Or check if any of the options contain the frequency
            if (plan.options && plan.options.some(option =>
                option.toLowerCase().includes(`${frequency} weeks`)
            )) {
                return true;
            }

            return false;
        });
    }

    /**
     * Show a message to the user
     * @param {HTMLElement} nearElement - Element to position the message near
     * @param {string} message - Message to display
     * @param {string} type - Message type (info, error, success)
     * @deprecated - Use showSuccessIndicationInDropdown or showErrorWithinDropdown instead
     */
    showMessage(nearElement, message, type = 'info') {
        console.log(`Message (${type}): ${message}`);
        // No-op - functionality replaced by showSuccessIndicationInDropdown and showErrorWithinDropdown
    }

    /**
     * Hide the message
     * @deprecated - Use showSuccessIndicationInDropdown or showErrorWithinDropdown instead
     */
    hideMessage() {
        // No-op - functionality replaced by showSuccessIndicationInDropdown and showErrorWithinDropdown
    }

    // Update subscription button text to simple "Subscribe & Save"
    updateSubscriptionButtonText() {
        const buttons = document.querySelectorAll('[data-subscription-button]');

        buttons.forEach(button => {
            // Set consistent text
            button.textContent = 'Subscribe & Save';
        });
    }
}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
    new CartSubscriptionManager();
});

// Also initialize when the cart drawer is opened
document.addEventListener('cart:opened', () => {
    // Give the cart drawer time to populate its content
    setTimeout(() => {
        // Refresh all subscription elements in the cart
        const cartManager = new CartSubscriptionManager();
        cartManager.refreshSubscriptions();
        cartManager.initDiscountDisplay();
    }, 300);
}); 